import { Controller } from "stimulus";
import { Draggable } from "@shopify/draggable";
import Rails from '@rails/ujs';

export default class extends Controller {
  static values = { programId: String };
  static outlets = ['overview--zoom'];

  connect() {
    const container = this.element;

    this.draggable = new Draggable(container, {
      draggable: ".item",
      handle: ".handle",
      mirror: {
        constrainDimensions: true,
      }
    });

    this.draggable.on('mirror:created', this._onMirrorCreated.bind(this));

    this.draggable.on('mirror:move', this._onMirrorMove.bind(this));

    this.draggable.on("drag:move", this._onDragMove.bind(this));

    this.draggable.on("drag:over", this._onDragOver.bind(this));

    this.draggable.on("drag:out", this._onDragOut.bind(this));

    this.draggable.on("drag:stop", this._onDragStop.bind(this));

    this._boundMouseHandler = this._onMouseMove.bind(this);

    this.over = undefined;
    this.mirror = null;
    this.dragIconOffsetX = null;
    this.dragIconOffsetY = null;
    this.zoomLevel = this.overviewZoomOutlet.zoomLevelValue;
  }

  disconnect() {
    this.draggable.destroy();
  }

  changeZoomLevel(newZoomLevel) {
    this.zoomLevel = newZoomLevel;
  }

  _onMirrorCreated(event) {
    const { mirror, _sourceContainer, source, _originalSource } = event.data;

    source.querySelector('.card-cover').classList.remove('hidden');

    if (mirror.classList.contains('js-overview-module')) {
      const card = mirror.querySelector('.card-content');
      card.classList.remove('border-slate-300');
      card.classList.add('border-purple-600');
      card.classList.add('border-dashed');
    } else {
      mirror.classList.remove('border-slate-200');
      mirror.classList.add('border-purple-600');
      mirror.classList.add('border-dashed');
    }

    mirror.classList.add('z-50');
  }

  _onMirrorMove(event) {
    // Cancelling the event means that we avoid the extra calculations made to reposition the
    // mirror after this event is run. Instead, the positioning is handled in _onMouseMove.
    event.cancel();

    if (this.mirror !== null) {
      return;
    }

    // Mirror variable is needed to set the correct coordinates of the mirror when cursor is moved.
    this.mirror = event.data.mirror;

    const source = event.data.source;
    const sourceDimensions = source.getBoundingClientRect();
    const newX = sourceDimensions.x / this.zoomLevel;
    const newY = sourceDimensions.y / this.zoomLevel;

    this.mirror.style.transform = `translate3d(${newX}px, ${newY}px, 0)`;

    document.addEventListener('mousemove', this._boundMouseHandler);

    // this.dragIconOffsetX and this.dragIconOffsetY are used to correctly position the cursor
    // on top of the drag icon when the mouse is moved.
    const dragIconDimensions = source.querySelector('[data-test-icon="drag_drop"]').getBoundingClientRect();
    this.dragIconOffsetX = dragIconDimensions.x - sourceDimensions.x + (dragIconDimensions.width / 2);
    this.dragIconOffsetY = dragIconDimensions.y - sourceDimensions.y + (dragIconDimensions.height / 2);
  }

  _onMouseMove(event) {
    const newX = event.clientX / this.zoomLevel - this.dragIconOffsetX;
    const newY = event.clientY / this.zoomLevel - this.dragIconOffsetY;

    this.mirror.style.transform = `translate3d(${newX}px, ${newY}px, 0)`;
  }

  _onDragMove(event) {
    const source = event.data.source;
    let over = this.over

    if (!over) {
      return
    }

    if (!source.classList.contains('js-overview-module')) {
      if (over.classList.contains('js-overview-module')) {
        let topParentType = over.dataset.moduleType;

        if (!this._elementCanBeDropped(source, topParentType)) {
          return false;
        }

        // TODO see if it's worth keeping this piece of code
        // if (over.querySelectorAll('[id*="js-overview-content-page-card--"]').length) {
        //   return false;
        // }
      } else {
        let topParentType = over.closest('.js-overview-module').dataset.moduleType;

        if (!this._elementCanBeDropped(source, topParentType)) {
          return false;
        }
      }
    }

    if (source.classList.contains('js-overview-module')) {
      this._dragProgramModule(event, source, over);
    } else {
      this._dragContentPage(event, source, over);
    }
  }

  _onDragOver(event) {
    const { source, over } = event.data;

    if (over) {
      if (source.classList.contains('js-overview-module') && !over.classList.contains('js-overview-module')) {
        this.over = over.closest('.js-overview-module')
      } else {
        this.over = over;
      }

      this._removeAllDividers();
    }
  }

  _onDragOut(event) {
    if (!event.data.source.classList.contains('js-overview-module')) {
      this.over = null;
    }
  }

  _onDragStop(event) {
    if (this.over) {
      const draggedElement = event.data.source;
      const originalSource = event.data.originalSource;

      if (draggedElement.classList.contains('js-overview-content-page-card') && !this.over.classList.contains('js-overview-content-page-card')) {
        this._insertIntoProgramModule(draggedElement);
      } else {
        this._insertBeforeOrAfterElement(event, draggedElement, originalSource);
      }
    }

    // Clean up
    this._removeAllDividers();
    this.over = null;
    this.contentPageIndex = null;
    this.before = null;
    this.after = null;
    this.mirror = null;
    this.dragIconOffsetX = null;
    this.dragIconOffsetY = null;

    document.removeEventListener('mousemove', this._boundMouseHandler);
  }

  _reorderElement(elementType, elementId, otherElementId, placement) {
    const url = `/admin/programs/${this.programIdValue}/overview/${elementType}/${elementId}/move`;

    const body = new FormData();
    body.append('src_drag_and_drop', true);

    if (placement === 'after') {
      body.append('after_id', otherElementId);
    } else {
      body.append('before_id', otherElementId);
    }

    this._performRequest(
      url, 'PATCH', body, document.querySelector('.draggable--original')
    );
  }

  _moveContentPageIntoProgramModule(draggedElement, programModuleId) {
    const contentPageId = draggedElement.dataset.elementId;
    const url = `/admin/programs/${this.programIdValue}/overview/content_pages/${contentPageId}/move`;

    const body = new FormData();
    body.append('src_drag_and_drop', true);
    body.append('module_id', programModuleId);

    this._performRequest(url, 'PATCH', body, document.querySelector('.draggable--original'));
  }

  _dragProgramModule(event, source, over) {
    const sensorData = event.data.sensorEvent.data;
    const container = document.getElementsByClassName('overview-cards-container')[0];
    const threshold = 150;
    const scrollSpeed = 20;
    const windowWidth = window.innerWidth;
    const { clientX } = sensorData;
    const mouseX = clientX;
    const crossedBoundaries = this._calculateOverlapPercentage(sensorData, over);

    // Scroll the container if the mouse is near the edges
    if (mouseX < threshold) {
      container.scrollBy(-scrollSpeed, 0);
    } else if (mouseX > windowWidth - threshold) {
      container.scrollBy(scrollSpeed, 0);
    }

    // Check if the mouse is over the left half or right half of the over element
    if (crossedBoundaries.hasCrossedHalfwayX) {
      over.classList.remove('divider-before');
      over.classList.add('divider-after');
      this.before = false;
      this.after = true;
    } else if (source !== over) {
      over.classList.remove('divider-after');
      over.classList.add('divider-before');

      this.before = true;
      this.after = false;
    }
  }

  _dragContentPage(event, source, over) {
    const sensorData = event.data.sensorEvent.data;
    const crossedBoundaries = this._calculateOverlapPercentage(sensorData, over);
    const overIndex = this._index(over);

    if (over.classList.contains('js-overview-module')) {
      if (!over.querySelectorAll('[id*="js-overview-content-page-card-"]').length) {
        if (!over.querySelector('.overview-cp-card-placeholder')) {
          const placeholder = document.createElement('div');
          placeholder.classList.add(
            'overview-cp-card-placeholder'
          );

          const container = over.querySelector('.js-overview-draggable-cp-cards');
          container.appendChild(placeholder);
        }
      }
    }

    if (crossedBoundaries.hasCrossedHalfwayY) {
      over.classList.remove('divider-above');
      over.classList.add('divider-below');
      this.before = false;
      this.after = true;
      this.contentPageIndex = overIndex + 1
    } else if (source !== over) {
      over.classList.remove('divider-below');
      over.classList.add('divider-above');

      this.before = true;
      this.after = false;

      this.contentPageIndex = overIndex;
    }
  }

  _insertIntoProgramModule(draggedElement) {
    const overElement = this.over;
    let topParentType = overElement.dataset.moduleType;

    let programModuleId = overElement.closest('.js-overview-module')
      .dataset.elementId;

    if (!this._elementCanBeDropped(draggedElement, topParentType)) {
      return false;
    }

    const container = overElement.querySelector('.js-overview-draggable-cp-cards');

    if (container.children.length === 0) {
      container.appendChild(draggedElement);
    } else {
      container.insertBefore(draggedElement, container.children[this.contentPageIndex]);
    }

    this._moveContentPageIntoProgramModule(draggedElement, programModuleId);
    draggedElement.getElementsByClassName('card-cover')[0].classList.add('hidden');

    // Clean up
    this.over = null;
  }

  _insertBeforeOrAfterElement(event, draggedElement, originalSource) {
    const overElement = this.over;
    const parentNode = overElement.parentNode;

    if (!draggedElement.classList.contains('js-overview-module')) {
      let topParentType = overElement.closest('.js-overview-module').dataset.moduleType;

      if (!this._elementCanBeDropped(draggedElement, topParentType)) {
        this._removeAllDividers();

        return false;
      }
    }

    if (this.before) {
      parentNode.insertBefore(draggedElement, overElement);

      this._reorderElement(
        draggedElement.dataset.elementType,
        draggedElement.dataset.elementId,
        overElement.dataset.elementId,
        'before'
      );
    } else if (this.after) {
      const afterElement = overElement.nextElementSibling;

      parentNode.insertBefore(draggedElement, afterElement);

      this._reorderElement(
        draggedElement.dataset.elementType,
        draggedElement.dataset.elementId,
        overElement.dataset.elementId,
        'after'
      );
    }

    // remove the card cover
    const cover = draggedElement.getElementsByClassName('card-cover')[0];
    cover.classList.add('hidden');
  }

  _elementCanBeDropped(draggedElement, topParentType) {
    let canBeDropped = false;

    draggedElement.dataset.allowedModuleTypes.split(',').forEach(item => {
      if (item.toLowerCase() === topParentType.toLowerCase()) {
        canBeDropped = true;
      }
    });

    return canBeDropped;
  }

  _removeAllDividers() {
    document.querySelectorAll('.divider-before, .divider-after, .divider-below, .divider-above').forEach((elem) => {
      elem.classList.remove('divider-before')
      elem.classList.remove('divider-after')
      elem.classList.remove('divider-above')
      elem.classList.remove('divider-below')
    })

    document.querySelectorAll('.overview-cp-card-placeholder').forEach((elem) => {
      elem.remove();
    })
  }

  _performRequest(url, method, body, element) {
    Rails.ajax({
      url: url,
      type: method,
      data: body,
      headers: {
        'Content-Type': 'application/json'
      },
      dataType: "script",
      success: () => {
        this._highlightElement(element);
      }
    });
  }

  _highlightElement(elem) {
    if (elem.classList.contains('js-overview-module')) {
      elem = elem.querySelector('.card-content');
    }

    document.dispatchEvent(new CustomEvent('promote:highlight', {
      detail: { element: elem }
    }));
  }

  _calculateOverlapPercentage(event, over) {
    const { clientX, clientY } = event;
    const overRect = over.getBoundingClientRect();

    // Calculate the midpoint of the over element
    const midPointX = overRect.left + overRect.width / 2;
    const midPointY = overRect.top + overRect.height / 2;

    // Check if the mouse is over the top half or bottom half of the over element
    const hasCrossedHalfwayX = clientX > midPointX;
    const hasCrossedHalfwayY = clientY > midPointY;

    return { hasCrossedHalfwayX, hasCrossedHalfwayY };
  }

  _index(elem) {
    return Array.prototype.indexOf.call(elem.parentNode.children, elem);
  }
}
